import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { IconLoader2 } from "@tabler/icons-react";
import Portal from "App/Portal/Portal";
import Auth from "App/Auth/Auth";
import AdminAuth from "App/Auth/AdminAuth/AdminAuth";
import Preview from "App/Auth/Preview/Preview";
import { usePortalSettings } from "_contexts/usePortalSettings";

function App() {
  const { loading: portalLoading, companyId } = usePortalSettings();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [isMasquerade, setIsMasquerade] = useState(false);
  const [masqueradeAdminEmail, setMasqueradeAdminEmail] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const adjustForKeyboard = () => {
      const viewportHeight = window.innerHeight;
      document.documentElement.style.setProperty(
        "--vh",
        `${viewportHeight * 0.01}px`
      );

      if (window.innerHeight < window.outerHeight * 0.7) {
        // Keyboard is open
        document.body.classList.add("keyboard-open");
      } else {
        // Keyboard is closed
        document.body.classList.remove("keyboard-open");
      }
    };

    window.addEventListener("resize", adjustForKeyboard);
    adjustForKeyboard(); // Run on mount

    return () => {
      window.removeEventListener("resize", adjustForKeyboard);
    };
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem("JWT");
    const savedAdminToken = localStorage.getItem("ADMIN_JWT");
    
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const expiry = payload.exp * 1000;
        
        // Check if token is valid and is a customer portal token
        if (Date.now() < expiry && payload.customer) {
          setApplicationId(payload.customer.application_id);
          setIsAuthenticated(true);
          // Check if this is a masquerade session
          if (payload.customer.masquerade) {
            setIsMasquerade(true);
            setMasqueradeAdminEmail(payload.customer.masquerade_admin_email);
            // Restore admin token if available
            if (savedAdminToken) {
              try {
                const adminPayload = JSON.parse(atob(savedAdminToken.split(".")[1]));
                const adminExpiry = adminPayload.exp * 1000;
                if (Date.now() < adminExpiry) {
                  setAdminToken(savedAdminToken);
                } else {
                  localStorage.removeItem("ADMIN_JWT");
                }
              } catch {
                localStorage.removeItem("ADMIN_JWT");
              }
            }
          }
        } else {
          // Token expired or invalid type
          localStorage.removeItem("JWT");
          localStorage.removeItem("ADMIN_JWT");
        }
      } catch (e) {
        // Invalid token format
        localStorage.removeItem("JWT");
        localStorage.removeItem("ADMIN_JWT");
      }
    }
    setAuthLoading(false);
  }, []);

  const handleAuthenticated = useCallback((token, savedAdminToken = null) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.customer) {
        setApplicationId(payload.customer.application_id);
        setIsAuthenticated(true);
        // Check if this is a masquerade session
        if (payload.customer.masquerade) {
          setIsMasquerade(true);
          setMasqueradeAdminEmail(payload.customer.masquerade_admin_email);
          // Store admin token for switch user functionality
          if (savedAdminToken) {
            setAdminToken(savedAdminToken);
            localStorage.setItem("ADMIN_JWT", savedAdminToken);
          }
        }
      }
    } catch (e) {
      console.error("Failed to parse token:", e);
    }
  }, []);

  const handleSwitchUser = useCallback((newToken) => {
    // Store the new customer token
    localStorage.setItem("JWT", newToken);
    
    try {
      const payload = JSON.parse(atob(newToken.split(".")[1]));
      if (payload.customer) {
        // Navigate to the new application and reload to refresh all data
        window.location.href = `/${payload.customer.application_id}`;
      }
    } catch (e) {
      console.error("Failed to parse token:", e);
      // Fallback: reload current page
      window.location.reload();
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("JWT");
    localStorage.removeItem("ADMIN_JWT");
    setIsAuthenticated(false);
    setApplicationId(null);
    setIsMasquerade(false);
    setMasqueradeAdminEmail(null);
    setAdminToken(null);
  }, []);

  // Wait for both portal settings and auth check to complete
  // Also wait for companyId to be available (required for app to function)
  if (authLoading || portalLoading || !companyId) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
          backgroundColor: "#efefef",
        }}
      >
        <IconLoader2
          size={120}
          strokeWidth={0.5}
          color="#000000"
          style={{ animation: "spin 0.9s linear infinite" }}
        />
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const globalProps = {
    onLogout: handleLogout,
    onSwitchUser: handleSwitchUser,
    isMasquerade,
    masqueradeAdminEmail,
    adminToken,
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth route */}
        <Route
          path="/"
          element={
            isAuthenticated && applicationId ? (
              <Navigate to={`/${applicationId}`} replace />
            ) : (
              <Auth onAuthenticated={handleAuthenticated} />
            )
          }
        />

        {/* Admin auth route for masquerade */}
        <Route
          path="/admin"
          element={
            isAuthenticated && applicationId ? (
              <Navigate to={`/${applicationId}`} replace />
            ) : (
              <AdminAuth onAuthenticated={handleAuthenticated} />
            )
          }
        />

        {/* Preview route for company admin portal preview */}
        {/* Always render Preview - it handles token exchange and redirects */}
        <Route
          path="/preview"
          element={<Preview onAuthenticated={handleAuthenticated} />}
        />

        {/* Portal routes - require authentication */}
        <Route
          path="/:applicationId/*"
          element={
            isAuthenticated ? (
              <Portal {...globalProps} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
